import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from "react";
import TextView from "./Typography";
import { Box } from "@mui/material";
import { generateRandomId, isEmpty } from "../../helpers/Functions";
import displayToast from "./DisplayToast";
import { FileHandlerProviderContext } from "../../contexts/contexts";
import { uploadFileOpenAI } from "../../app/crud/ai.crud";

const MAX_FILE_SIZE_MB = 20; // Maximum file size in MB
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const DEFAULT_ACCEPTED_TYPES = [
	"application/pdf", // PDF documents
	"text/plain", // Plain text files
	"text/csv", // CSV files
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
	"application/msword", // .doc
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
	"application/vnd.ms-excel", // .xls
	"image/jpeg", // JPEG images
	"image/png", // PNG images
	"image/gif", // GIF images
	"image/webp", // WebP images
];

export const FileHandlerProvider = ({
	children,
	parentComponent, // optional ref
	acceptedTypes = DEFAULT_ACCEPTED_TYPES,
}) => {
	const [files, setFiles] = useState([]);
	const [isDragging, setIsDragging] = useState(false);

	const fileInputAttachmentRef = useRef(null);

	const fileToFileObj = (file) => {
		return {
			id: generateRandomId(),
			file,
		};
	}

	const syncFiles = (inputFiles = []) => {
		if (inputFiles.length === 0) {
			return;
		}

		const newFiles = [];

		for(const inpFile of inputFiles) {
			if (inpFile.size > MAX_FILE_SIZE_BYTES) {
				displayToast(`${inpFile.name}: exceeds ${MAX_FILE_SIZE_MB}MB limit. `);
				continue;
			}

			if (!acceptedTypes.includes(inpFile.type)) {
				displayToast(`${inpFile.name}: unsupported file type (${inpFile.type}). `);
				continue;
			}

			const fileWithMeta = {
				uploaded: false,
				file_id: "",
				file_name: inpFile.name,
				uploadParams: {
					inpFile,
					type: inpFile.type,
					name: inpFile.name,
					size: inpFile.size,
				},
			};

			newFiles.push(fileToFileObj(fileWithMeta));
		}

		setFiles((prev) => [...prev, ...newFiles]);
	}

	const removeFile = (id) => {
		setFiles((prev) => prev.filter((f) => f.id !== id));
	};

	const clearFiles = () => {
		setFiles([]);
	};

	const openFileSelector = () => {
		fileInputAttachmentRef?.current?.click?.();
	}

	useEffect(() => {
		const parent = parentComponent?.current || document.body;

		const handleDragOver = (e) => {
			e.preventDefault();
			setIsDragging(true);
		};

		const handleDragLeave = (e) => {
			e.preventDefault();
			setIsDragging(false);
		};

		const handleDrop = (e) => {
			e.preventDefault();
			setIsDragging(false);

			syncFiles(e.dataTransfer.files);
		};

		parent.addEventListener("dragover", handleDragOver);
		parent.addEventListener("dragleave", handleDragLeave);
		parent.addEventListener("drop", handleDrop);

		return () => {
			parent.removeEventListener("dragover", handleDragOver);
			parent.removeEventListener("dragleave", handleDragLeave);
			parent.removeEventListener("drop", handleDrop);
		};
	}, [parentComponent, acceptedTypes]);

	const nonUploadedFiles = useMemo(() => files.filter((f) => !f.file.uploaded), [files]);

	useEffect(() => {
		if (!isEmpty(nonUploadedFiles)) {
			let params = {
				ai_files: nonUploadedFiles.map((f) => f?.file?.uploadParams),
			};

			uploadFileOpenAI(params).then((response) => {
				if (response.data.init[0].status === "error") {
					displayToast("error", response.data.init[0]["message"]);
				} else {
					let data = response.data.data;

					const uploadedMap = new Map();
					data.forEach((d) => uploadedMap.set(d.file_name, d));

					setFiles(
						files.map((file) => {
							const uploadedFile = uploadedMap.get(file.file.file_name);

							if (!isEmpty(uploadedFile)) {
								const uploadParams = file.file.uploadParams;

								const fileWithMeta = {
									uploaded: true,
									file_url: uploadedFile.file_u_r_l,
									file_id: uploadedFile.open_a_i_file_id,
									file_name: uploadParams.name,
									uploadParams: {
										inpFile: uploadParams.inpFile,
										type: uploadParams.type,
										name: uploadParams.name,
										size: uploadParams.size,
									},
								};
								
								return fileToFileObj(fileWithMeta);
							}

							return file;
						})
					);
				}
			});
		}
	}, [nonUploadedFiles]);

	return (
		<FileHandlerProviderContext.Provider value={{ 
			files, 
			setFiles,
			isDragging,
			clearFiles,
			syncFiles, 
			removeFile,
			nonUploadedFiles,
			openFileSelector,
		}}>
			<input
				ref={fileInputAttachmentRef}
				id="file-upload-input"
				type="file"
				multiple
				onChange={(e) => {
					syncFiles(e.target.files);
				}}
				hidden
				accept={acceptedTypes.join(",")}
			/>

			{children}

			{isDragging && (
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						background: "rgba(0, 0, 0, 0.25)",
						border: "2px dashed var(--theme-primary)",
						fontWeight: 600,
						fontSize: "1rem",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						pointerEvents: "none",
						zIndex: 9999,
					}}
				>
					<TextView fontsize="xl" fontcolor="white" fontweight="semibold">Drop to upload</TextView>
					<TextView fontcolor="white" fontsize="medium" fontweight="medium">Your files will be processed instantly</TextView>
				</Box>
			)}
		</FileHandlerProviderContext.Provider>
	);
};
